import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { History as HistoryIcon, ShoppingCart, DollarSign, Receipt, LogIn, Package, Landmark, Building2, KeyRound, Filter } from 'lucide-react';

const History = () => {
  const [logs, setLogs] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({});

  useEffect(() => { fetchHistory(); }, [filter]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { action: filter } : {};
      const res = await api.get('/history', { params });
      setLogs(res.data.logs || []);
      setGrouped(res.data.groupedByDate || {});
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const getIcon = (action) => {
    if (action?.includes('SALE')) return <ShoppingCart size={16} className="text-emerald-400" />;
    if (action?.includes('PURCHASE')) return <DollarSign size={16} className="text-blue-400" />;
    if (action?.includes('EXPENSE')) return <Receipt size={16} className="text-orange-400" />;
    if (action?.includes('LOGIN')) return <LogIn size={16} className="text-cyan-400" />;
    if (action?.includes('STOCK')) return <Package size={16} className="text-purple-400" />;
    if (action?.includes('LOAN')) return <Landmark size={16} className="text-yellow-400" />;
    if (action?.includes('ASSET')) return <Building2 size={16} className="text-pink-400" />;
    if (action?.includes('PASSWORD')) return <KeyRound size={16} className="text-red-400" />;
    return <HistoryIcon size={16} className="text-gray-400" />;
  };

  const getBgColor = (action) => {
    if (action?.includes('SALE')) return 'bg-emerald-500/10';
    if (action?.includes('PURCHASE')) return 'bg-blue-500/10';
    if (action?.includes('EXPENSE')) return 'bg-orange-500/10';
    if (action?.includes('LOGIN')) return 'bg-cyan-500/10';
    if (action?.includes('STOCK')) return 'bg-purple-500/10';
    if (action?.includes('LOAN')) return 'bg-yellow-500/10';
    if (action?.includes('ASSET')) return 'bg-pink-500/10';
    if (action?.includes('PASSWORD')) return 'bg-red-500/10';
    return 'bg-gray-500/10';
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' });
  };

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'SALE_CREATED', label: 'Sales' },
    { key: 'PURCHASE_CREATED', label: 'Purchases' },
    { key: 'EXPENSE_ADDED', label: 'Expenses' },
    { key: 'LOGIN', label: 'Logins' },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      <div className="flex items-center gap-2">
        <HistoryIcon size={24} className="text-emerald-400" />
        <h2 className="text-xl font-bold text-white">History</h2>
      </div>

      {/* Filters */}
      <div className="flex gap-1 bg-[#0d1410] rounded-lg p-1 overflow-x-auto">
        {filters.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap transition ${filter === f.key ? 'bg-emerald-500/20 text-emerald-400' : 'text-gray-400'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {logs.length === 0 ? (
        <div className="bg-[#0d1410] border border-white/5 rounded-xl p-12 text-center">
          <HistoryIcon size={48} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">No activities yet</p>
          <p className="text-gray-600 text-xs mt-1">Your business activities will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([date, activities]) => (
            <div key={date} className="bg-[#0d1410] border border-white/5 rounded-xl overflow-hidden">
              <div className="p-3 border-b border-white/5 bg-white/[0.02]">
                <p className="text-white text-sm font-semibold">📅 {date}</p>
              </div>
              <div className="divide-y divide-white/5">
                {activities.map((log) => (
                  <div key={log.id} className="p-3 flex items-center gap-3">
                    <div className={`${getBgColor(log.action)} p-2 rounded-lg flex-shrink-0`}>
                      {getIcon(log.action)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-300 text-sm truncate">{log.details}</p>
                      <p className="text-gray-500 text-[10px]">{log.action?.replace(/_/g, ' ')}</p>
                    </div>
                    <span className="text-gray-500 text-xs flex-shrink-0">{formatTime(log.createdAt)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default History;
